import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useAuth } from './AuthContext';
import { productService, authService } from '../api/axios';

const InternalChatContext = createContext(null);

const INTERNAL_ROLES = ['ADMIN', 'CURATOR', 'EMPLOYEE'];
const SESSION_PAGE_SIZE = 20;
const MESSAGE_PAGE_SIZE = 50;

export function InternalChatProvider({ children }) {
    const { user } = useAuth();
    const isInternal = user && INTERNAL_ROLES.includes(user.role);

    // Sessions
    const [sessions, setSessions] = useState([]);
    const [sessionsPage, setSessionsPage] = useState(0);
    const [sessionsHasMore, setSessionsHasMore] = useState(false);

    const [mySessions, setMySessions] = useState([]);
    const [mySessionsPage, setMySessionsPage] = useState(0);
    const [mySessionsHasMore, setMySessionsHasMore] = useState(false);

    const [activeSessionId, setActiveSessionId] = useState(null);

    // Messages — keyed by sessionId
    const [messages, setMessages] = useState({});
    const [messagesPage, setMessagesPage] = useState({});
    const [messagesHasMore, setMessagesHasMore] = useState({});

    // Presence
    const [onlineUsers, setOnlineUsers] = useState([]);
    const [allTeamMembers, setAllTeamMembers] = useState([]);

    // UI
    const [unreadCount, setUnreadCount] = useState(0);
    const [directedToMe, setDirectedToMe] = useState(new Set());
    const [panelOpen, setPanelOpen] = useState(false);
    const [connected, setConnected] = useState(false);

    const stompRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const sessionSubsRef = useRef({});

    const rawApiBase = process.env.REACT_APP_API_BASE_URL || '';
    const wsBase = rawApiBase ? rawApiBase.replace(/\/api\/?$/, '') : '';
    const token = localStorage.getItem('jwtToken');

    // ── Heartbeat / presence ──────────────────────────────────────────────────

    const sendHeartbeat = useCallback(async () => {
        try {
            const res = await productService.post('internal-chat/heartbeat');
            setOnlineUsers(res.data || []);
        } catch {
            // silent
        }
    }, []);

    const fetchTeamMembers = useCallback(async () => {
        try {
            const res = await authService.get('auth/internal-users');
            setAllTeamMembers(res.data || []);
        } catch {
            // non-critical
        }
    }, []);

    // ── Session fetching ──────────────────────────────────────────────────────

    const fetchSessions = useCallback(async () => {
        try {
            const [openRes, myRes] = await Promise.all([
                productService.get(`internal-chat/sessions?page=0&size=${SESSION_PAGE_SIZE}`),
                productService.get(`internal-chat/my-sessions?page=0&size=${SESSION_PAGE_SIZE}`),
            ]);
            setSessions(openRes.data.content || []);
            setSessionsHasMore(!openRes.data.last);
            setSessionsPage(0);

            setMySessions(myRes.data.content || []);
            setMySessionsHasMore(!myRes.data.last);
            setMySessionsPage(0);
        } catch (e) {
            console.error('Failed to load chat sessions:', e);
        }
    }, []);

    const loadMoreSessions = useCallback(async () => {
        try {
            const nextPage = sessionsPage + 1;
            const res = await productService.get(`internal-chat/sessions?page=${nextPage}&size=${SESSION_PAGE_SIZE}`);
            setSessions(prev => [...prev, ...(res.data.content || [])]);
            setSessionsHasMore(!res.data.last);
            setSessionsPage(nextPage);
        } catch (e) {
            console.error('Failed to load more sessions:', e);
        }
    }, [sessionsPage]);

    const loadMoreMySessions = useCallback(async () => {
        try {
            const nextPage = mySessionsPage + 1;
            const res = await productService.get(`internal-chat/my-sessions?page=${nextPage}&size=${SESSION_PAGE_SIZE}`);
            setMySessions(prev => [...prev, ...(res.data.content || [])]);
            setMySessionsHasMore(!res.data.last);
            setMySessionsPage(nextPage);
        } catch (e) {
            console.error('Failed to load more my-sessions:', e);
        }
    }, [mySessionsPage]);

    // ── Message fetching ──────────────────────────────────────────────────────

    const subscribeToSession = useCallback((sessionId) => {
        if (!stompRef.current?.connected || sessionSubsRef.current[sessionId]) return;
        const sub = stompRef.current.subscribe(`/topic/chat/${sessionId}`, (frame) => {
            const msg = JSON.parse(frame.body);
            setMessages(prev => ({
                ...prev,
                [sessionId]: [...(prev[sessionId] || []), msg],
            }));
        });
        sessionSubsRef.current[sessionId] = sub;
    }, []);

    const openSession = useCallback(async (sessionId) => {
        setActiveSessionId(sessionId);

        if (!messages[sessionId]) {
            try {
                const res = await productService.get(
                    `internal-chat/sessions/${sessionId}/messages?page=0&size=${MESSAGE_PAGE_SIZE}`
                );
                // Backend returns DESC (newest first) — reverse for chronological display
                const content = [...(res.data.content || [])].reverse();
                setMessages(prev => ({ ...prev, [sessionId]: content }));
                setMessagesHasMore(prev => ({ ...prev, [sessionId]: !res.data.last }));
                setMessagesPage(prev => ({ ...prev, [sessionId]: 0 }));
            } catch (e) {
                console.error('Failed to load messages:', e);
            }
        }

        subscribeToSession(sessionId);
    }, [messages, subscribeToSession]);

    const loadEarlierMessages = useCallback(async (sessionId) => {
        try {
            const currentPage = messagesPage[sessionId] || 0;
            const nextPage = currentPage + 1;
            const res = await productService.get(
                `internal-chat/sessions/${sessionId}/messages?page=${nextPage}&size=${MESSAGE_PAGE_SIZE}`
            );
            // Backend DESC → reverse → prepend to existing (which is already chronological)
            const older = [...(res.data.content || [])].reverse();
            setMessages(prev => ({ ...prev, [sessionId]: [...older, ...(prev[sessionId] || [])] }));
            setMessagesHasMore(prev => ({ ...prev, [sessionId]: !res.data.last }));
            setMessagesPage(prev => ({ ...prev, [sessionId]: nextPage }));
        } catch (e) {
            console.error('Failed to load earlier messages:', e);
        }
    }, [messagesPage]);

    // ── WebSocket connection ───────────────────────────────────────────────────

    useEffect(() => {
        if (!isInternal || !token) return;

        const client = new Client({
            webSocketFactory: () => new SockJS(`${wsBase}/ws/internal-chat`),
            connectHeaders: { Authorization: `Bearer ${token}` },
            reconnectDelay: 5000,
            onConnect: () => {
                setConnected(true);
                fetchSessions();
                sendHeartbeat();
                fetchTeamMembers();

                client.subscribe('/topic/sessions/new', (frame) => {
                    const session = JSON.parse(frame.body);
                    setSessions(prev => [session, ...prev.filter(s => s.id !== session.id)]);
                    if (session.createdBy === user.username) {
                        setMySessions(prev => [session, ...prev.filter(s => s.id !== session.id)]);
                    } else {
                        setUnreadCount(prev => prev + 1);
                    }
                    if (session.directedTo === user.username) {
                        setDirectedToMe(prev => new Set([...prev, session.id]));
                    }
                });

                client.subscribe('/topic/sessions/resolved', (frame) => {
                    const resolvedId = JSON.parse(frame.body);
                    setSessions(prev => prev.filter(s => s.id !== resolvedId));
                    setMySessions(prev => prev.map(s =>
                        s.id === resolvedId ? { ...s, status: 'RESOLVED' } : s
                    ));
                });

                client.subscribe('/topic/presence', (frame) => {
                    setOnlineUsers(JSON.parse(frame.body) || []);
                });
            },
            onDisconnect: () => setConnected(false),
            onStompError: (frame) => {
                console.error('STOMP error:', frame.headers?.message);
            },
        });

        client.activate();
        stompRef.current = client;
        heartbeatTimerRef.current = setInterval(sendHeartbeat, 30_000);

        return () => {
            clearInterval(heartbeatTimerRef.current);
            Object.values(sessionSubsRef.current).forEach(sub => sub?.unsubscribe());
            sessionSubsRef.current = {};
            client.deactivate();
            stompRef.current = null;
        };
    }, [isInternal, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Session actions ───────────────────────────────────────────────────────

    const createSession = useCallback((title, directedTo) => {
        if (!stompRef.current?.connected) return;
        stompRef.current.publish({
            destination: '/app/chat/create',
            body: JSON.stringify({ title, directedTo: directedTo || null }),
        });
    }, []);

    const sendMessage = useCallback((sessionId, content) => {
        if (!stompRef.current?.connected) return;
        stompRef.current.publish({
            destination: `/app/chat/${sessionId}/send`,
            body: JSON.stringify({ content }),
        });
    }, []);

    const resolveSession = useCallback(async (sessionId) => {
        try {
            await productService.post(`internal-chat/sessions/${sessionId}/resolve`);
            setActiveSessionId(null);
        } catch (e) {
            console.error('Failed to resolve session:', e);
        }
    }, []);

    // ── Panel controls ────────────────────────────────────────────────────────

    const openPanel = useCallback(() => {
        setPanelOpen(true);
        setUnreadCount(0);
        setDirectedToMe(new Set());
    }, []);

    const closePanel = useCallback(() => {
        setPanelOpen(false);
        setActiveSessionId(null);
    }, []);

    const closeThread = useCallback(() => setActiveSessionId(null), []);

    // ── Context value ─────────────────────────────────────────────────────────

    const value = {
        // Sessions
        sessions, sessionsHasMore, loadMoreSessions,
        mySessions, mySessionsHasMore, loadMoreMySessions,
        activeSessionId,
        // Messages
        messages, messagesHasMore, loadEarlierMessages,
        // Presence
        onlineUsers, allTeamMembers,
        // UI
        unreadCount, directedToMe, panelOpen, connected, user,
        // Actions
        openPanel, closePanel, closeThread, openSession,
        createSession, sendMessage, resolveSession,
    };

    if (!isInternal) return <>{children}</>;

    return (
        <InternalChatContext.Provider value={value}>
            {children}
        </InternalChatContext.Provider>
    );
}

export const useInternalChat = () => useContext(InternalChatContext);
